import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { getFacilityContextSignal } from "./facilityContextSignal";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { notifications } from "@mantine/notifications";
import { assumedRoleRef } from "../contexts/AssumedRoleContext";
import { roleDisplayName } from "./roleDisplay";
import { desktop } from "./platform";
import { clearLocalSession } from "./cognitoClients";
import { DeadlineExceededError, withDeadline } from "./withDeadline";

// On desktop the backend URL comes from the main process (electron-store), so it
// is changeable without a rebuild and always matches the main-process CSP. On
// the web it stays the baked env value.
const apiUrl =
  desktop?.backendUrl ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:3001/api";

// ─── Time limits ─────────────────────────────────────────────────────────────
//
// axios waits forever by default, and on the iPad a socket left over from
// before a lock/wake neither answers nor errors — every "stuck spinner" bug
// on the device so far has been an unbounded wait. So every request through
// these clients gets a limit unless its caller set one:
//
// - REQUEST_TIMEOUT_MS equals the ALB idle timeout (livingsyncs-infra
//   deployments/ecs/main.tf, alb_idle_timeout_seconds = 120). The ALB timer
//   is IDLE time, this one is TOTAL time — they coincide for this app's JSON
//   routes because each sends its whole response at the end (nothing flows
//   until then), so one that went quiet for longer was already cut off at
//   the ALB. The slowest, the AI routes, cap themselves at 100s for the same
//   reason (backend lib/ai-provider.ts AI_REQUEST_TIMEOUT_MS). A future
//   streaming JSON endpoint would need its own `timeout`.
// - Uploads and file downloads keep bytes flowing, which the ALB doesn't cut,
//   and on a slow connection they can legitimately take minutes.
const REQUEST_TIMEOUT_MS = 120_000;
const TRANSFER_TIMEOUT_MS = 10 * 60_000;
// Reading the session before a request can itself refresh tokens over the
// network — before axios's own timeout has started.
const SESSION_LOOKUP_TIMEOUT_MS = 10_000;

/** Exported for tests. Leaves an explicit per-call timeout alone. */
export function applyDefaultTimeout(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.timeout) {
    const isTransfer =
      config.data instanceof FormData ||
      config.responseType === "blob" ||
      config.responseType === "arraybuffer";
    config.timeout = isTransfer ? TRANSFER_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
  }
  return config;
}

function sessionWithin(options?: { forceRefresh?: boolean }) {
  return withDeadline(
    fetchAuthSession(options),
    SESSION_LOOKUP_TIMEOUT_MS,
    "session lookup",
  );
}

const api = axios.create({
  baseURL: apiUrl,
  headers: { "Content-Type": "application/json" },
});

// ─── CSRF token — in-memory only, never localStorage ─────────────────────────

let csrfToken: string | null = null;

// The ID token the request interceptor last attached. Kept only so the logout
// audit can be sent with the credentials the session had BEFORE sign-out
// clears them (see sendLogoutAudit) — never read for anything else.
let lastIdToken: string | null = null;

// Bare instance for the logout audit: no interceptors, because by the time a
// request interceptor would run, sign-out may already have cleared the
// session it needs (see sendLogoutAudit). Exported so tests can spy on it.
export const logoutAuditClient = axios.create({
  baseURL: apiUrl,
  headers: { "Content-Type": "application/json" },
  timeout: REQUEST_TIMEOUT_MS,
});

// Mutations to these paths are CSRF-exempt server-side (mirrors
// CSRF_EXEMPT_PREFIXES in backend middleware.ts). They are PUBLIC endpoints
// reachable while UNAUTHENTICATED — an invitee accepting an invite, the
// landing-page demo/feedback forms — so we must not pre-fetch a CSRF token for
// them: GET /api/auth/csrf is auth-only (US-045) and would 401 for an
// anonymous caller, tripping the global logout-redirect in the response
// interceptor and aborting the in-flight mutation before it is even sent.
// Paths are relative to the `/api` baseURL.
const CSRF_EXEMPT_PATHS = ["/invitations/accept", "/demo-request", "/feedback"];

// Dedicated instance for fetching the CSRF token. It injects the Cognito Bearer
// token (so authenticated callers get a 200) but has NO response interceptor —
// a 401 here (anonymous invitee on the public accept flow) must never trigger
// the global sign-out + redirect that the main instance does. fetchCsrfToken
// swallows the rejection and the mutation proceeds token-less, which is fine
// for the CSRF-exempt public routes that are the only ones that reach here
// without a session.
//
// Exported (like `api` itself) so tests can `vi.spyOn(csrfClient, "get")` —
// any mutation that reaches fetchCsrfToken() without this mocked issues a
// REAL network call to /auth/csrf, which hangs to the test timeout in a
// network-isolated test env instead of failing fast (see apiClientReadOnly
// test's history for the incident this note exists to prevent).
export const csrfClient = axios.create({
  baseURL: apiUrl,
  headers: { "Content-Type": "application/json" },
  timeout: REQUEST_TIMEOUT_MS,
});
csrfClient.interceptors.request.use(async (config) => {
  try {
    const session = await sessionWithin();
    const token = session.tokens?.idToken?.toString();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // Not authenticated — the GET will 401 and fetchCsrfToken swallows it.
  }
  return config;
});

/** Fetch a CSRF token from the server and store it in memory. */
export async function fetchCsrfToken(): Promise<void> {
  try {
    const { data } = await csrfClient.get<{ csrfToken: string }>("/auth/csrf");
    csrfToken = data.csrfToken;
  } catch (error) {
    // A cached-but-locally-valid ID token can still be rejected server-side
    // as invalid/expired (LIVINGSYNCS-BACKEND-C: ~2k invalid_token auth_failed
    // events, mostly long-lived desktop sessions where Amplify's cached token
    // had gone stale). Retry once with a forced session refresh — mirrors the
    // main `api` instance's 401 handling below. For an anonymous caller (the
    // public CSRF-exempt flows) forceRefresh throws with no session, so this
    // stays a no-op there and falls through to the existing silent recovery.
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      try {
        const session = await sessionWithin({ forceRefresh: true });
        const token = session.tokens?.idToken?.toString();
        if (token) {
          const { data } = await csrfClient.get<{ csrfToken: string }>(
            "/auth/csrf",
            { headers: { Authorization: `Bearer ${token}` } },
          );
          csrfToken = data.csrfToken;
          return;
        }
      } catch {
        // Fall through — non-fatal, the next mutation's 403 auto-retry recovers.
      }
    }
    // Non-fatal — the next mutation will receive a 403 and retry
  }
}

/** Clear the in-memory CSRF token (call on logout). */
export function clearCsrfToken(): void {
  csrfToken = null;
}

/**
 * Forget the credentials captured for the logout audit. Call alongside ANY
 * signOut() — including the wrong-portal bounces, which stay in the same JS
 * context instead of hard-reloading — so a token can never outlive the
 * session it belongs to on a shared device.
 */
export function clearCapturedCredentials(): void {
  csrfToken = null;
  lastIdToken = null;
}

/**
 * Deliberate logout: signs out via Amplify, then records a best-effort audit
 * event. Use this for user-initiated logout (e.g. the logout button).
 * The 401-interceptor path (expired session) skips the audit call because
 * the token is already gone and the backend cannot authenticate the request.
 *
 * signOut() and desktop.purge() run IN PARALLEL, not sequentially (fixed
 * 2026-09-13, second fix on this same day for this same function — the first
 * (reordering the audit POST after signOut()) was NOT sufficient). Reading
 * @aws-amplify/auth's own signOut.ts directly: its non-global path
 * (clientSignOut()) makes its OWN network call to Cognito's revoke-token
 * endpoint FIRST, and only clears local tokens (tokenOrchestrator.clearTokens())
 * AFTER that call settles. That network call has the EXACT SAME "can hang
 * indefinitely after a device lock/wake cycle" failure mode this file's own
 * /auth/logout POST had — just hidden inside a third-party SDK we don't
 * control, and only sometimes: when Cognito's revoke-token call happens to
 * come back fast, `await signOut()` finishes fine within the caller's
 * bounded race and everything works. When it hangs, Amplify's OWN local
 * session data is STILL considered valid by getCurrentUser() on the very
 * next page load, and LoginPage.tsx's own check bounces the "signed out"
 * user straight back to "/". Confirmed both ways live: this is why the
 * first fix (build 51) looked fixed on some tests and not others — it
 * reduced the failure window but never eliminated it, since signOut()'s own
 * internal hang was never addressed.
 *
 * desktop.purge() clears the SAME Keychain-backed storage Amplify's token
 * provider reads from (see keychainTokenStore.ts / cognito.ts's
 * setKeyValueStorage() wiring) via a native plugin call — not a network
 * request — so it is not subject to the same post-lock hang. Starting it
 * CONCURRENTLY with signOut(), instead of after it, means the actual
 * persisted session gets invalidated on schedule even when signOut()'s own
 * internal Cognito call is stuck — closing the gap no amount of re-ordering
 * OUR OWN calls could reach, since the hang lives inside Amplify's code.
 */
export async function logoutAndSignOut(): Promise<void> {
  // HIPAA §164.312(b) logoff audit. Started FIRST but never awaited, carrying
  // the credentials captured right now. It used to be sent last, through the
  // normal client, after clearCsrfToken() and signOut() had removed both the
  // CSRF token and the Bearer token — so every one was rejected (~1,700 CSRF
  // "Token missing" 403s on preprod over 2026-09-16..19; zero logoff rows).
  // Not awaiting it keeps the 2026-09-13 guarantee below: nothing delays
  // signOut() and purge(). The ID token stays valid server-side after a
  // sign-out (JWTs are verified offline), so the audit lands even if it
  // arrives after signOut() has revoked the refresh token.
  sendLogoutAudit();
  assumedRoleRef.current = null;
  clearCapturedCredentials();
  // Best-effort: a failed/raced Amplify signOut must never reject out of here.
  // If it did, the inactivity-logout mutation (useLogout) would skip its
  // redirect + cache-clear and strand the user on the current page with a live
  // Cognito session — the stale state that then breaks the next sign-in with
  // UserAlreadyAuthenticatedException (SCRUM-140). Sign-in self-heals from that,
  // but we avoid creating it in the first place.
  const signOutPromise = signOut().catch(() => {
    // Non-fatal — the in-memory session is cleared on the next full page load
    // (useLogout redirects via window.location), and sign-in recovers regardless.
  });
  // Desktop: wipe storage partition + cache + encrypted tokens (US-013 AC12).
  // Started CONCURRENTLY with signOut() above, not after it — see this
  // function's own doc comment for why sequencing this after signOut() left
  // a real, confirmed-live gap.
  const purgePromise = desktop
    ? desktop.purge().catch(() => {
        // Non-fatal — sign-out already cleared the in-app session
      })
    : Promise.resolve();
  await Promise.allSettled([signOutPromise, purgePromise]);
}

/**
 * Fire-and-forget POST /auth/logout with the Bearer + CSRF token held at
 * call time. Skipped when the Bearer is missing (nothing to authenticate
 * with — it would only produce a rejected request). Losing it to a
 * navigation race is acceptable; delaying sign-out for it is not.
 *
 * On a CSRF rejection it fetches a fresh token and retries once: the app
 * fetches its CSRF token ONCE at sign-in (CsrfInitializer) while the token
 * expires after an hour (csrfMiddleware CSRF_TTL_MS), so any session that
 * signs out more than an hour after its last mutation — including the 8h
 * hard-ceiling logoff, which is structurally longer than the TTL — would
 * otherwise still lose its logoff row. GET /auth/csrf is CSRF-exempt and
 * needs only the Bearer, which stays valid for its own lifetime after
 * sign-out (JWTs verify offline). Still never awaited by the caller.
 */
function sendLogoutAudit(): void {
  const idToken = lastIdToken;
  if (!idToken) return;
  const auth = { Authorization: `Bearer ${idToken}` };
  const post = (csrf: string) =>
    logoutAuditClient.post("/auth/logout", undefined, {
      headers: { ...auth, "X-CSRF-Token": csrf },
    });

  void (async () => {
    try {
      if (csrfToken) {
        await post(csrfToken);
        return;
      }
    } catch (error) {
      if (!isCsrfRejection(error)) return; // any other failure: give up
    }
    try {
      const { data } = await logoutAuditClient.get<{ csrfToken: string }>(
        "/auth/csrf",
        { headers: auth },
      );
      await post(data.csrfToken);
    } catch {
      // Non-fatal — best-effort audit.
    }
  })();
}

/** A 403 from the CSRF layer (missing/malformed/expired/invalid/origin). */
function isCsrfRejection(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: unknown } } }
  )?.response;
  return (
    response?.status === 403 &&
    typeof response.data?.error === "string" &&
    response.data.error.includes("CSRF")
  );
}

// ─── Request interceptor ──────────────────────────────────────────────────────

const MUTATION_METHODS = new Set(["post", "put", "delete", "patch"]);

// SCRUM-148: the only mutations allowed while assuming a role — session-level
// endpoints (enter/exit the role, change viewed facility, sign out). Paths are
// relative to the `/api` baseURL. Mirrors the backend middleware allowlist.
const ASSUMED_ROLE_WRITE_ALLOWLIST = ["/auth/assume-role", "/auth/logout"];

api.interceptors.request.use(
  async (config) => {
    // Applied here, not as a separate interceptor: tests across the apps
    // reach this handler as the instance's first request interceptor.
    applyDefaultTimeout(config);
    // SCRUM-148: role assumption is read-only. Short-circuit mutations locally
    // with a clear toast rather than round-tripping to the backend's read-only
    // 403. Only the session-level endpoints needed while assuming (enter/exit
    // the role, change viewed facility, sign out) are allowed — profile edits
    // (/auth/me) and everything else are blocked. Mirrors the backend allowlist
    // in middleware.ts; the backend is the authoritative enforcement.
    // Match the path only (drop any query string) and anchor to exact-or-
    // path-boundary — NOT a loose prefix — so a future /auth/assume-role-history
    // can't auto-exempt itself. Mirrors the backend middleware allowlist.
    const path = (config.url ?? "").split("?")[0];
    const isAssumeSessionWrite = ASSUMED_ROLE_WRITE_ALLOWLIST.some(
      (p) => path === p || path.startsWith(p + "/"),
    );
    if (
      assumedRoleRef.current &&
      config.method &&
      MUTATION_METHODS.has(config.method.toLowerCase()) &&
      !isAssumeSessionWrite
    ) {
      notifications.show({
        color: "yellow",
        title: "Read-only mode",
        message: `You're viewing as ${roleDisplayName(
          assumedRoleRef.current,
        )}. Exit the assumed role to make changes.`,
      });
      return Promise.reject(
        new axios.Cancel("Read-only while viewing as another role"),
      );
    }

    // Inject Cognito ID token
    try {
      const session = await sessionWithin();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        lastIdToken = token;
      }
    } catch (err) {
      // A stalled lookup is a connection problem, not a missing session:
      // sending the request token-less would 401 and sign the user out.
      // Fail it the way a timed-out request fails (no response → the
      // global "check your connection" copy).
      if (err instanceof DeadlineExceededError) {
        throw new AxiosError(err.message, AxiosError.ECONNABORTED, config);
      }
      // Not authenticated — request will 401 and response interceptor handles it
    }

    // Inject assumed role from in-memory context ref (never localStorage)
    if (assumedRoleRef.current) {
      config.headers["X-Assumed-Role"] = assumedRoleRef.current;
    }

    // US-179: carry the facility context the UI is showing, so the backend can
    // record a switch from a request the user is already making instead of a
    // separate fire-and-forget call that failed silently (F-013). AUDIT-ONLY —
    // the backend never lets this widen access. Must stay in the CORS
    // Access-Control-Allow-Headers list (BOTH copies in middleware.ts) or
    // preflight blocks every cross-origin call in production.
    const facilityContext = getFacilityContextSignal();
    if (facilityContext) {
      config.headers["X-Facility-Context"] = facilityContext;
    }

    // US-183: report the running app's version/platform so the backend can
    // hard-block a stale iPad build. `desktop` is undefined on the web, so
    // this only ever fires under a native shell (Electron or Capacitor) —
    // web callers, always on the latest continuously-deployed frontend,
    // are never gated. Must stay in the CORS Access-Control-Allow-Headers
    // list (both copies in proxy.ts) or preflight blocks it in production.
    if (desktop) {
      config.headers["X-App-Version"] = desktop.getAppVersion();
      config.headers["X-App-Platform"] = desktop.getPlatform();
    }

    // Inject CSRF token for mutations. If we don't hold one yet (e.g. the first
    // mutation after load, before CsrfInitializer resolved, or after the token
    // lapsed), fetch one synchronously rather than sending the request
    // token-less and getting it rejected (SCRUM-114). The fetch hits a GET
    // endpoint, so it does not re-enter this mutation branch.
    if (config.method && MUTATION_METHODS.has(config.method.toLowerCase())) {
      // Skip the pre-fetch for CSRF-exempt public paths — an anonymous invitee
      // has no session, so fetching a token would 401 and trip the logout
      // redirect (see CSRF_EXEMPT_PATHS). These routes don't validate CSRF.
      const isCsrfExempt = CSRF_EXEMPT_PATHS.some(
        (p) => path === p || path.startsWith(p + "/"),
      );
      if (!isCsrfExempt) {
        if (!csrfToken) {
          await fetchCsrfToken();
        }
        if (csrfToken) {
          config.headers["X-CSRF-Token"] = csrfToken;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor ─────────────────────────────────────────────────────

let isLoggingOut = false;

/**
 * Reset the sign-out latch. FOR TESTS ONLY.
 *
 * `isLoggingOut` de-duplicates concurrent 401s so a burst of failed requests
 * triggers one sign-out, and it clears itself on a 1000ms real timer. That is
 * correct in the browser but leaks across tests: a test that triggers a
 * sign-out leaves the latch set, and any test running within the next second
 * is silently short-circuited — `signOut` is never called and the assertion
 * fails with "0 times". Whether it bites depends on wall-clock time between
 * tests, which is why it looked like load-related flakiness rather than the
 * order dependence it is.
 */
export function __resetAuthLogoutLatchForTests(): void {
  isLoggingOut = false;
}

/**
 * US-183: set by the response interceptor when the backend reports this
 * device's app version as below the configured minimum. A plain ref, not
 * React state — apiClient runs outside React, and the flag only ever flips
 * forward for the lifetime of the page (once blocked, a device stays
 * blocked until reload). `AppUpdateRequiredGate` polls this to render the
 * full-screen blocking view.
 */
export const appUpdateRequiredRef: { current: { minVersion: string } | null } =
  {
    current: null,
  };

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // US-183: recognize the version-gate's reject shape BEFORE the 401
    // handling below — this is not an auth failure and must never sign the
    // user out or touch CSRF state. The device stays blocked, not logged
    // out, so it recovers automatically the moment its binary is current.
    if (
      error.response?.status === 426 &&
      error.response?.data?.code === "UPDATE_REQUIRED"
    ) {
      appUpdateRequiredRef.current = {
        minVersion: error.response.data.minVersion,
      };
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // SCRUM-122: the first request after an idle gap (laptop sleep, long
      // inactivity) can go out with a stale access token and 401 even though
      // the Cognito refresh token is still valid. Force-refresh the session
      // and retry the request ONCE before destroying an otherwise-good
      // session — only sign out when the refresh or the retry also fails.
      if (!error.config?._authRetried && !isLoggingOut) {
        try {
          const session = await sessionWithin({ forceRefresh: true });
          const token = session.tokens?.idToken?.toString();
          if (token) {
            error.config._authRetried = true;
            error.config.headers.Authorization = `Bearer ${token}`;
            return api.request(error.config);
          }
        } catch (err) {
          // A refresh that never answered says nothing about the session —
          // fail this one request instead of signing the user out.
          if (err instanceof DeadlineExceededError)
            return Promise.reject(error);
          // Refresh failed — the session really is gone; fall through.
        }
      }
      if (isLoggingOut) return Promise.reject(error);
      isLoggingOut = true;
      try {
        assumedRoleRef.current = null;
        clearCapturedCredentials();
        // Bounded: signOut() revokes over the network BEFORE clearing local
        // tokens, and can hang after an iPad wake — which left the user on a
        // dead page with no redirect. On a stall, clear locally (the same
        // fallback logoutAndSignOut() gets from purge()) and move on.
        try {
          await withDeadline(signOut(), SESSION_LOOKUP_TIMEOUT_MS, "sign-out");
        } catch {
          await clearLocalSession();
        }
        // Avoid re-issuing the redirect if a 401 from this very /login
        // page's own request fires (e.g. a stale query still in flight).
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      } finally {
        setTimeout(() => {
          isLoggingOut = false;
        }, 1000);
      }
    }

    // Auto-refresh CSRF token on 403 and retry the request once
    if (
      error.response?.status === 403 &&
      error.response?.data?.error?.includes("CSRF") &&
      !error.config._csrfRetried
    ) {
      error.config._csrfRetried = true;
      await fetchCsrfToken();
      if (csrfToken) {
        error.config.headers["X-CSRF-Token"] = csrfToken;
        return api.request(error.config);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
